import React, { useState } from 'react';
import { Box, Typography, Stack, ToggleButton, ToggleButtonGroup, Paper } from '@mui/material';

interface Props {
  spot?: number;
  currentPnl?: number;
}

export const PayoffDiagram: React.FC<Props> = ({ spot = 24842.5, currentPnl = 4514 }) => {
  const [viewMode, setViewMode] = useState<'ORIGINAL' | 'ADJUSTED'>('ORIGINAL');

  // Strangle strikes: 24600 PE, 25100 CE (Entry premium ~156.6 pts = ₹3,915 max profit)
  const isAdjusted = viewMode === 'ADJUSTED';
  const shortPut = 24600;
  const shortCall = 25100;
  const wingPut = 24400;
  const wingCall = 25300;

  // Max profit & max loss values
  const maxProfit = isAdjusted ? 2800 : 3915;
  const maxLoss = isAdjusted ? 2200 : 15000;
  const beLower = isAdjusted ? 24488 : 24443;
  const beUpper = isAdjusted ? 25212 : 25256;

  // Generate SVG path coordinates
  // Spot range from 24000 to 25800 (1800 pts width)
  const minSpot = 24000;
  const maxSpot = 25800;
  const width = 600;
  const height = 240;
  const zeroY = 140; // 0 PnL level

  const getX = (price: number) => ((price - minSpot) / (maxSpot - minSpot)) * width;
  const getY = (pnl: number) => zeroY - (pnl / (isAdjusted ? 4000 : 8000)) * 80;

  // Generate polyline points
  const points: [number, number][] = [];
  for (let p = minSpot; p <= maxSpot; p += 25) {
    let pnl = 0;
    if (!isAdjusted) {
      // Strangle
      const callPayoff = -Math.max(0, p - shortCall);
      const putPayoff = -Math.max(0, shortPut - p);
      pnl = (156.6 + callPayoff + putPayoff) * 25;
    } else {
      // Iron Condor
      const callSpread = -Math.max(0, p - shortCall) + Math.max(0, p - wingCall);
      const putSpread = -Math.max(0, shortPut - p) + Math.max(0, wingPut - p);
      pnl = (112.0 + callSpread + putSpread) * 25;
    }
    points.push([getX(p), getY(pnl)]);
  }

  const pathD = points.reduce((acc, [x, y], i) => `${acc} ${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`, '');

  return (
    <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid #E2E8F0', bgcolor: '#FFFFFF' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E293B' }}>
            PAYOFF DIAGRAM & DYNAMIC ADJUSTMENT OVERLAY
          </Typography>
          <Typography variant="caption" sx={{ color: '#64748B' }}>
            {isAdjusted ? 'Iron Condor with Protective Wings (Defined Risk)' : 'Original Short Strangle (Unhedged Wings)'}
          </Typography>
        </Box>

        <ToggleButtonGroup
          size="small"
          value={viewMode}
          exclusive
          onChange={(_, val) => val && setViewMode(val)}
          sx={{ height: 30 }}
        >
          <ToggleButton value="ORIGINAL" sx={{ fontSize: '0.72rem', fontWeight: 600 }}>
            Original Strangle
          </ToggleButton>
          <ToggleButton value="ADJUSTED" sx={{ fontSize: '0.72rem', fontWeight: 600 }}>
            Adjusted (Wings Added)
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {/* SVG Payoff Curve */}
      <Box sx={{ width: '100%', overflowX: 'auto', bgcolor: '#F8FAFC', p: 1.5, borderRadius: 1.5, border: '1px solid #E2E8F0' }}>
        <svg viewBox={`0 0 ${width} ${height}`} style={{ width: '100%', height: 'auto', display: 'block' }}>
          {/* Zero PnL axis */}
          <line x1="0" y1={zeroY} x2={width} y2={zeroY} stroke="#94A3B8" strokeWidth="1" strokeDasharray="3 3" />
          <text x="5" y={zeroY - 4} fill="#64748B" fontSize="10">
            ₹0 P&L
          </text>

          {/* Current Spot line */}
          <line x1={getX(spot)} y1="0" x2={getX(spot)} y2={height} stroke="#0284C7" strokeWidth="1.5" strokeDasharray="4 2" />
          <text x={getX(spot) + 4} y="20" fill="#0284C7" fontSize="10" fontWeight="700">
            Spot: ₹{spot.toFixed(0)}
          </text>

          {/* Breakeven lines */}
          <line x1={getX(beLower)} y1="40" x2={getX(beLower)} y2={height} stroke="#64748B" strokeWidth="1" strokeDasharray="2 2" />
          <text x={getX(beLower) - 30} y={height - 5} fill="#64748B" fontSize="9">
            BE: {beLower}
          </text>

          <line x1={getX(beUpper)} y1="40" x2={getX(beUpper)} y2={height} stroke="#64748B" strokeWidth="1" strokeDasharray="2 2" />
          <text x={getX(beUpper) - 10} y={height - 5} fill="#64748B" fontSize="9">
            BE: {beUpper}
          </text>

          {/* Payoff path */}
          <path d={pathD} fill="none" stroke={isAdjusted ? '#10B981' : '#0284C7'} strokeWidth="2.5" />

          {/* Current Spot point */}
          <circle cx={getX(spot)} cy={getY(currentPnl)} r="5" fill="#EF4444" stroke="#FFFFFF" strokeWidth="1.5" />
        </svg>
      </Box>

      {/* Payoff Key Metrics */}
      <Stack direction="row" justifyContent="space-between" sx={{ mt: 2, pt: 1.5, borderTop: '1px solid #F1F5F9' }}>
        <Box>
          <Typography variant="caption" sx={{ color: '#64748B' }}>
            MAX PROFIT
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 700, color: '#16A34A' }}>
            +₹{maxProfit.toLocaleString('en-IN')}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" sx={{ color: '#64748B' }}>
            MAX LOSS
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 700, color: '#DC2626' }}>
            {isAdjusted ? `-₹${maxLoss.toLocaleString('en-IN')}` : 'UNLIMITED (Capped at ₹15k)'}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" sx={{ color: '#64748B' }}>
            LOWER BREAKEVEN
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#1E293B' }}>
            ₹{beLower}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" sx={{ color: '#64748B' }}>
            UPPER BREAKEVEN
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#1E293B' }}>
            ₹{beUpper}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
};
